/**
 * Goal Completion Panel
 *
 * Displays episode goals with completion status.
 * Shows all goals from the episode and marks completed ones with strikethrough.
 */

import { type EpisodeResponse } from '../services/episodesApi';

interface GoalCompletionPanelProps {
  episode?: EpisodeResponse;
  completedGoalIds: Set<string>;
}

export function GoalCompletionPanel({ episode, completedGoalIds }: GoalCompletionPanelProps) {
  // Show loading or empty state if no episode data
  if (!episode || !episode.goals || episode.goals.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 h-[600px] flex flex-col">
        <h2 className="text-xl font-semibold mb-4">Episode Goals</h2>
        <p className="text-gray-500 text-sm">
          {!episode ? 'Loading episode goals...' : 'No goals defined for this episode.'}
        </p>
      </div>
    );
  }

  const completedCount = Array.from(completedGoalIds).length;
  const totalGoals = episode.goals.length;

  return (
    <div className="bg-white rounded-lg shadow-md p-6 h-[600px] flex flex-col">
      <div className="mb-4">
        <h2 className="text-xl font-semibold mb-2">Episode Goals</h2>
        <p className="text-sm text-gray-600">
          {completedCount} of {totalGoals} completed
        </p>
      </div>

      <div className="flex-1 overflow-y-auto space-y-3">
        {episode.goals.map((goal, index) => {
          const goalIndex = String(index); // Use actual goal index (0, 1, 2, ...)
          const isCompleted = completedGoalIds.has(goalIndex);

          return (
            <div
              key={index}
              className={`p-4 rounded-lg border-2 transition-all ${
                isCompleted ? 'bg-green-50 border-green-300' : 'bg-gray-50 border-gray-200'
              }`}
            >
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-0.5">
                  {isCompleted ? (
                    <span className="text-green-600 text-xl">✓</span>
                  ) : (
                    <span className="text-gray-400 text-xl">○</span>
                  )}
                </div>
                <div className="flex-grow min-w-0">
                  <p
                    className={`text-sm ${
                      isCompleted ? 'line-through text-gray-500' : 'text-gray-900'
                    }`}
                  >
                    {goal}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
